import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { 
  WiThermometer,
  WiHumidity,
  WiStrongWind,
  WiBarometer,
  WiDaySunny,
  WiNightClear,
  WiDayCloudy,
  WiNightAltCloudy,
  WiCloudy,
  WiRain,
  WiThunderstorm,
  WiSnow,
  WiFog,
  WiThermometerExterior,
  WiThermometerInternal
} from 'react-icons/wi'
import { designTokens } from '@/styles/designTokens'

const WeatherContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  gap: ${designTokens.spacing.sm};
  font-family: ${designTokens.typography.fontFamily.primary};
  padding: ${designTokens.spacing.lg};
`

const TemperatureLine = styled.div`
  font-size: ${props => props.$compact ? designTokens.typography.fontSize['3xl'] : designTokens.typography.fontSize['4xl']};
  font-weight: ${designTokens.typography.fontWeight.bold};
  color: ${designTokens.colors.text.primary};
  text-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
  text-align: center;
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};
  
  svg {
    color: ${designTokens.colors.primary.blue};
    filter: drop-shadow(0 0 4px ${designTokens.colors.primary.blue}40);
  }
`

const IconLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${designTokens.spacing.sm};
`

const WeatherIcon = styled.img`
  width: ${props => props.$compact ? '40px' : '56px'};
  height: ${props => props.$compact ? '40px' : '56px'};
`

const ConditionLine = styled.div`
  font-size: ${designTokens.typography.fontSize.base};
  color: ${designTokens.colors.text.secondary};
  text-align: center;
  opacity: 0.9;
  text-transform: capitalize;
  font-weight: ${designTokens.typography.fontWeight.medium};
`

const WeatherDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${designTokens.spacing.xs};
  width: 100%;
  margin-top: ${designTokens.spacing.sm};
`

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.colors.text.muted};
  gap: ${designTokens.spacing.sm};
`

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.xs};
  flex: 1;
  justify-content: center;
`

const DetailIcon = styled.div`
  color: ${designTokens.colors.primary.blue};
  font-size: ${props => props.$compact ? designTokens.typography.fontSize.lg : designTokens.typography.fontSize.xl};
  filter: drop-shadow(0 0 4px ${designTokens.colors.primary.blue}40);
`

const DetailValue = styled.span`
  font-weight: ${designTokens.typography.fontWeight.medium};
  color: ${designTokens.colors.text.secondary};
`

const MinMaxTemp = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${props => props.$compact ? '10px' : '12px'};
  color: #ccc;
`

const TempRange = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Weather = ({ data, compact = false }) => {
  const getWeatherIcon = (iconCode) => {
    if (!iconCode) return <WiDaySunny />;
    
    const iconMap = {
      '01d': <WiDaySunny />,      // Céu limpo - sol
      '01n': <WiNightClear />,    // Céu limpo - lua
      '02d': <WiDayCloudy />,     // Poucas nuvens - sol
      '02n': <WiNightAltCloudy />, // Poucas nuvens - lua
      '03d': <WiCloudy />,        // Nuvens dispersas
      '03n': <WiCloudy />,        // Nuvens dispersas
      '04d': <WiCloudy />,        // Nuvens quebradas
      '04n': <WiCloudy />,        // Nuvens quebradas
      '09d': <WiRain />,          // Chuva
      '09n': <WiRain />,          // Chuva
      '10d': <WiRain />,          // Chuva com sol
      '10n': <WiRain />,          // Chuva com lua
      '11d': <WiThunderstorm />,  // Trovão
      '11n': <WiThunderstorm />,  // Trovão
      '13d': <WiSnow />,          // Neve
      '13n': <WiSnow />,          // Neve
      '50d': <WiFog />,           // Névoa
      '50n': <WiFog />,           // Névoa
    };
    
    return iconMap[iconCode] || <WiDaySunny />;
  };
  if (!data) {
    return (
      <WeatherContainer
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <TemperatureLine $compact={compact}>
          <WiThermometer />
          --°C
        </TemperatureLine>
        <ConditionLine $compact={compact}>Sem dados</ConditionLine>
        {!compact && (
          <>
            <ConditionLine $compact={compact}>Sem dados</ConditionLine>
            <WeatherDetails>
              <DetailRow $compact={compact}>
                <DetailItem>
                  <DetailIcon $compact={compact}><WiHumidity /></DetailIcon>
                  <DetailValue>--%</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailIcon $compact={compact}><WiStrongWind /></DetailIcon>
                  <DetailValue>-- km/h</DetailValue>
                </DetailItem>
              </DetailRow>
            </WeatherDetails>
          </>
        )}
      </WeatherContainer>
    )
  }

  // Debug dos dados
  console.log('Weather data:', data)

  return (
    <WeatherContainer
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <TemperatureLine $compact={compact}>
        <WiThermometer />
        {data.temp ? Math.round(data.temp) : '--'}°C
      </TemperatureLine>
      
      <IconLine>
        <div style={{ display: 'flex', alignItems: 'center', gap: designTokens.spacing.sm }}>
          {data.icone && (
            <>
              <WeatherIcon 
                $compact={compact}
                src={`https://openweathermap.org/img/wn/${data.icone}@2x.png`}
                alt={data.condicao}
                onError={(e) => {
                  console.error('Erro ao carregar ícone do clima:', e.target.src);
                  e.target.style.display = 'none';
                  // Mostrar ícone de fallback
                  const fallbackIcon = e.target.parentNode.querySelector('.fallback-icon');
                  if (fallbackIcon) fallbackIcon.style.display = 'flex';
                }}
              />
              <div 
                className="fallback-icon" 
                style={{ 
                  display: 'none', 
                  color: designTokens.colors.primary.blue, 
                  fontSize: compact ? '40px' : '56px',
                  width: compact ? '40px' : '56px',
                  height: compact ? '40px' : '56px',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {getWeatherIcon(data.icone)}
              </div>
            </>
          )}
          <span style={{ 
            fontSize: compact ? designTokens.typography.fontSize.base : designTokens.typography.fontSize.lg, 
            color: designTokens.colors.text.secondary,
            fontWeight: designTokens.typography.fontWeight.medium
          }}>
            {data.condicao}
          </span>
        </div>
      </IconLine>
      
      {!compact && (
        <>
          <ConditionLine $compact={compact}>
            {data.descricao || data.condicao || 'Clima'}
          </ConditionLine>
          
          <WeatherDetails>
            <DetailRow $compact={compact}>
              <DetailItem>
                <DetailIcon $compact={compact}><WiHumidity /></DetailIcon>
                <DetailValue>{data.humidity ? `${data.humidity}%` : '--'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailIcon $compact={compact}><WiStrongWind /></DetailIcon>
                <DetailValue>{data.wind_speed ? `${Math.round(data.wind_speed)} km/h` : '--'}</DetailValue>
              </DetailItem>
            </DetailRow>
            
            <DetailRow $compact={compact}>
              <DetailItem>
                <DetailIcon $compact={compact}><WiBarometer /></DetailIcon>
                <DetailValue>{data.visibility ? `${Math.round(data.visibility / 1000)} km` : '--'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailIcon $compact={compact}><WiThermometerExterior /></DetailIcon>
                <DetailValue>{data.temp_min ? `${Math.round(data.temp_min)}°` : '--'}</DetailValue>
              </DetailItem>
            </DetailRow>
            
            <DetailRow $compact={compact}>
              <DetailItem>
                <DetailIcon $compact={compact}><WiThermometerInternal /></DetailIcon>
                <DetailValue>{data.temp_max ? `${Math.round(data.temp_max)}°` : '--'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailIcon $compact={compact}><WiDaySunny /></DetailIcon>
                <DetailValue>{data.sunrise || '--'}</DetailValue>
              </DetailItem>
            </DetailRow>
          </WeatherDetails>
        </>
      )}
    </WeatherContainer>
  )
}

export default Weather 